import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { CompletionRuleEvaluator } from '../services/CompletionRuleEvaluator';

/**
 * Shared hook for loading and transforming timeline tasks
 * Used by both Hub and Timeline pages to ensure consistency
 *
 * @param {string} userId - Current user ID
 * @param {Array} colleges - College context for enrichment
 * @param {Object} options - Optional configuration
 * @returns {Object} { timeline, loading, error, refetch }
 */
export const useTimelineTasks = (userId, colleges = [], options = {}) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const hasEvaluatedCompletionRulesRef = useRef(false);
  const collegesRef = useRef(colleges);

  // Keep colleges ref in sync
  useEffect(() => {
    collegesRef.current = colleges;
  }, [colleges]);

  // Helper: Parse local date
  const parseLocalDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }, []);

  // Helper: Get hard deadline from college
  const getHardDeadline = useCallback((college) => {
    if (!college?.global_colleges) return null;
    const round = college.application_round;
    const gc = college.global_colleges;

    switch(round) {
      case 'early_decision':
      case 'early_decision_1':
        return gc.ed_deadline;
      case 'early_decision_2':
        return gc.ed2_deadline;
      case 'early_action':
        return gc.ea_deadline;
      case 'regular_decision':
      default:
        return gc.rd_deadline;
    }
  }, []);

  // Main fetch and transform function
  const fetchTimeline = useCallback(async () => {
    if (!userId) {
      console.log('[useTimelineTasks] No userId provided');
      setLoading(false);
      setTimeline([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch timeline items with college data (consistent query for both pages)
      const { data: timelineData, error: timelineError } = await supabase
        .from('timeline_items')
        .select(`
          *,
          college:college_journey_colleges(
            id,
            name,
            category,
            application_round,
            priority_order,
            global_college_id,
            global_colleges(
              id,
              name,
              ed_deadline,
              ed2_deadline,
              ea_deadline,
              rd_deadline
            )
          )
        `)
        .eq('user_id', userId)
        .not('scheduled_date', 'is', null) // Must have a scheduled date
        .order('scheduled_date', { ascending: true });

      if (timelineError) throw timelineError;

      // Transform timeline items to match expected format
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const transformedTasks = (timelineData || []).map(item => {
        // Get college data from the joined college object or from colleges context
        const itemCollege = item.college;
        const contextCollege = collegesRef.current?.find(c => c.id === item.college_id);
        const college = itemCollege || contextCollege;

        const scheduledDate = item.scheduled_date;
        const dueDate = item.due_date;
        const taskDate = parseLocalDate(scheduledDate);

        // Calculate status flags
        const isCompleted = item.completion_status === 'completed';
        const isOverdue = taskDate && taskDate < today && !isCompleted;
        const isRescheduled = scheduledDate !== dueDate && dueDate;

        const hardDeadline = getHardDeadline(college);
        const isPastHardDeadline = hardDeadline && taskDate && taskDate > new Date(hardDeadline);
        const daysOverdue = isOverdue && taskDate ? Math.ceil((today - taskDate) / (1000 * 60 * 60 * 24)) : 0;

        return {
          ...item, // Spread ALL fields first (includes crisis fields, can_autogenerate, etc.)
          // Then override with computed/enriched values
          college_name: college?.name || item.college_name,
          application_round: college?.application_round || item.application_round,
          college_category: college?.category || item.college_category,
          college: itemCollege,

          // Status flags (consistent across both pages)
          isOverdue,
          isCompleted,
          isRescheduled,
          isPastHardDeadline,
          daysOverdue,

          // Date fields
          scheduled_date: scheduledDate,
          due_date: dueDate,
          display_date: scheduledDate
        };
      });

      console.log(`[useTimelineTasks] Loaded ${transformedTasks.length} tasks for user ${userId}`);

      setTimeline(transformedTasks);

      // Trigger completion rule evaluation after timeline loads (only once)
      if (!hasEvaluatedCompletionRulesRef.current && options.evaluateCompletionRules !== false) {
        try {
          console.log('[useTimelineTasks] 🔄 Triggering completion rule evaluation...');
          const completionResults = await CompletionRuleEvaluator.evaluateUserTasks(userId);
          console.log('[useTimelineTasks] ✅ Completion evaluation results:', completionResults);
          hasEvaluatedCompletionRulesRef.current = true;

          // If tasks were changed, refetch timeline to show updated completion status
          if (completionResults.completed > 0 || completionResults.incompleted > 0) {
            console.log(`[useTimelineTasks] ✅ ${completionResults.completed} completed, ${completionResults.incompleted} incompleted - refreshing`);
            // Refetch after a brief delay
            setTimeout(() => fetchTimeline(), 500);
          }
        } catch (evalError) {
          console.error('[useTimelineTasks] Error evaluating completion rules:', evalError);
          // Don't show user error for evaluation failures
        }
      }

    } catch (err) {
      console.error('[useTimelineTasks] Error fetching timeline:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [userId, parseLocalDate, getHardDeadline, options.evaluateCompletionRules]);

  // Fetch on mount and when userId changes
  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  // Return timeline data and utilities
  return {
    timeline,
    loading,
    error,
    refetch: fetchTimeline
  };
};
